import axios from 'axios';

const RAW_API_BASE = process.env.REACT_APP_BACKEND_URL || '';
const API_BASE = RAW_API_BASE.replace(/\/$/, '').replace(/\/api$/, '');
const API = `${API_BASE}/api`;

const api = axios.create({ baseURL: API });

api.interceptors.request.use((config) => {
  const token = localStorage.getItem('agency_os_token');
  if (token) config.headers.Authorization = `Bearer ${token}`;
  return config;
});

api.interceptors.response.use(
  (res) => res,
  (err) => {
    if (err.response?.status === 401) {
      localStorage.removeItem('agency_os_token');
      localStorage.removeItem('agency_os_user');
      window.location.href = '/login';
    }
    return Promise.reject(err);
  }
);

export const authApi = {
  login: (email, password) => api.post('/auth/login', { email, password }),
  signup: (data) => api.post('/auth/signup', data),
  register: (data) => api.post('/auth/register', data),
  me: () => api.get('/auth/me'),
  seed: () => {
    if (process.env.NODE_ENV === 'production') return Promise.reject(new Error('Seed endpoint disabled in production'));
    return api.post('/seed');
  },
};

export const subscriptionApi = {
  get: () => api.get('/settings/subscription'),
  getPlans: () => api.get('/plans'),
  createSubscription: (plan, billing_period = 'monthly') => api.post('/auth/create-subscription', { plan, billing_period }),
  createOrder: (plan) => api.post('/auth/create-order', { plan }),
  verifyPayment: (data) => api.post('/auth/verify-payment', data),
};

export const settingsApi = {
  getApiKeys: () => api.get('/settings/api-keys'),
  updateApiKeys: (data) => api.put('/settings/api-keys', data),
  getAgencyProfile: () => api.get('/settings/agency-profile'),
  updateAgencyProfile: (data) => api.put('/settings/agency-profile', data),
};

export const dashboardApi = { get: () => api.get('/activity/dashboard') };

export const clientsApi = {
  list: (params = {}) => {
    const qs = new URLSearchParams(params).toString();
    return api.get(`/clients${qs ? `?${qs}` : ''}`);
  },
  get: (id) => api.get(`/clients/${id}`),
  create: (data) => api.post('/clients', data),
  update: (id, data) => api.put(`/clients/${id}`, data),
  delete: (id) => api.delete(`/clients/${id}`),
};

export const brandBrainsApi = {
  get: (clientId) => api.get(`/clients/${clientId}/brand-brain`),
  upsert: (clientId, data) => api.post(`/clients/${clientId}/brand-brain`, data),
};

export const campaignsApi = {
  list: (params = {}) => {
    const qs = new URLSearchParams(params).toString();
    return api.get(`/campaigns${qs ? `?${qs}` : ''}`);
  },
  get: (id) => api.get(`/campaigns/${id}`),
  create: (data) => api.post('/campaigns', data),
  update: (id, data) => api.put(`/campaigns/${id}`, data),
  delete: (id) => api.delete(`/campaigns/${id}`),
};

export const contractsApi = {
  list: (params = {}) => {
    const qs = new URLSearchParams(params).toString();
    return api.get(`/contracts${qs ? `?${qs}` : ''}`);
  },
  listForClient: (clientId) => api.get(`/contracts?client_id=${encodeURIComponent(clientId)}`),
  get: (id) => api.get(`/contracts/${id}`),
  create: (data) => api.post('/contracts', data),
  update: (id, data) => api.put(`/contracts/${id}`, data),
  delete: (id) => api.delete(`/contracts/${id}`),
  generateDeliverables: (id, campaignId) => api.post(`/contracts/${id}/generate-deliverables`, { campaign_id: campaignId }),
};

export const deliverablesApi = {
  list: (params = {}) => {
    const qs = new URLSearchParams(params).toString();
    return api.get(`/deliverables${qs ? `?${qs}` : ''}`);
  },
  get: (id) => api.get(`/deliverables/${id}`),
  create: (data) => api.post('/deliverables', data),
  update: (id, data) => api.put(`/deliverables/${id}`, data),
  updateStatus: (id, status) => api.put(`/deliverables/${id}/status`, { status }),
  delete: (id) => api.delete(`/deliverables/${id}`),
  generateContent: (id, templateId, outputFormat = 'plain', prompt = '', idempotencyKey = '') => {
    const params = new URLSearchParams();
    if (templateId) params.set('template_id', templateId);
    if (outputFormat) params.set('output_format', outputFormat);
    if (prompt && prompt.trim()) params.set('prompt', prompt.trim());
    const qs = params.toString();
    return api.post(`/deliverables/${id}/generate${qs ? `?${qs}` : ''}`, null, idempotencyKey ? { headers: { 'Idempotency-Key': idempotencyKey } } : undefined);
  },
  getJobStatus: (jobId) => api.get(`/deliverables/jobs/${jobId}`),
  getVersions: (id) => api.get(`/deliverables/${id}/versions`),
  restoreVersion: (delivId, versionId) => api.post(`/deliverables/${delivId}/versions/${versionId}/restore`),
  exportHtml: (id) => api.get(`/deliverables/${id}/export/html`, { responseType: 'blob' }),
  exportPdf: (id) => api.get(`/deliverables/${id}/export/pdf`, { responseType: 'blob' }),
  share: (id) => api.post(`/deliverables/${id}/share`),
  uploadFile: (id, file) => {
    const form = new FormData();
    form.append('file', file);
    return api.post(`/deliverables/${id}/files`, form, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
  },
  listFiles: (id) => api.get(`/deliverables/${id}/files`),
  downloadFile: (id, fileId) => api.get(`/deliverables/${id}/files/${fileId}/download`, { responseType: 'blob' }),
  deleteFile: (id, fileId) => api.delete(`/deliverables/${id}/files/${fileId}`),
};

export const jobsApi = {
  get: (id) => api.get(`/deliverables/jobs/${id}`),
  cancel: (id) => api.post(`/deliverables/jobs/${id}/cancel`),
};

export const activityApi = { list: () => api.get('/activity') };

export const searchApi = {
  global: (query) => api.get(`/search?q=${encodeURIComponent(query)}`),
};

export const usersApi = {
  list: () => api.get('/users'),
  invite: (data) => api.post('/auth/invite', data),
  updateRole: (id, role) => api.put(`/users/${id}/role`, { role }),
  updateAccessProfile: (id, data) => api.put(`/users/${id}/access-profile`, data),
  deactivate: (id) => api.put(`/users/${id}/deactivate`),
  activate: (id) => api.put(`/users/${id}/activate`),
  delete: (id) => api.delete(`/users/${id}`),
  getSalesRoles: () => api.get('/sales/roles'),
  updateSalesRole: (userId, data) => api.patch(`/sales/team/${userId}/role`, data),
  updateDeliveryProfile: (userId, data) => api.put(`/users/${userId}/delivery-profile`, data),
};

export const templatesApi = {
  list: () => api.get('/templates'),
  get: (id) => api.get(`/templates/${id}`),
  create: (data) => api.post('/templates', data),
  update: (id, data) => api.put(`/templates/${id}`, data),
  delete: (id) => api.delete(`/templates/${id}`),
  seedDefaults: () => api.post('/templates/seed-defaults'),
};

export const portalClientsApi = {
  list: (clientId) => api.get(`/settings/portal-clients${clientId ? `?client_id=${clientId}` : ''}`),
  create: (data) => api.post('/settings/portal-clients', data),
  delete: (id) => api.delete(`/settings/portal-clients/${id}`),
};

export const reviewApi = {
  get: (token) => axios.get(`${API}/review/${token}`),
  action: (token, data) => axios.post(`${API}/review/${token}`, data),
  getAttachmentDownloadUrl: (token, fileId) => `${API}/review/${token}/files/${fileId}/download`,
};

export default api;


export const paperworkApi = {
  summary: (clientId) => api.get(`/paperwork/clients/${clientId}/summary`),
  generate: (clientId, data) => api.post(`/paperwork/clients/${clientId}/documents/generate`, data),
  markSigned: (documentId) => api.post(`/paperwork/documents/${documentId}/mark-signed`),
  send: (documentId, metadata = {}) => api.post(`/paperwork/documents/${documentId}/send`, { metadata }),
  preview: (documentId) => api.get(`/paperwork/documents/${documentId}/preview`, { responseType: 'blob' }),
  download: (documentId) => api.get(`/paperwork/documents/${documentId}/download`, { responseType: 'blob' }),
  sharePreview: (token) => api.get(`/paperwork/share/${token}`),
  remove: (documentId) => api.delete(`/paperwork/documents/${documentId}`),
  versions: (documentId) => api.get(`/paperwork/documents/${documentId}/versions`),
  createMom: (clientId, data) => api.post(`/paperwork/clients/${clientId}/mom`, data),
  createCreditNote: (clientId, data) => api.post(`/paperwork/clients/${clientId}/credit-note`, { ...data, document_type: 'credit_note' }),
  resolveMissingInfo: (clientId, data) => api.post(`/paperwork/clients/${clientId}/missing-info/resolve`, data),
};
export const paperworkTemplatesApi = {
  upload: (formData) => api.post('/paperwork-templates/upload', formData, { headers: { 'Content-Type': 'multipart/form-data' } }),
  list: (clientId, limit = 50, offset = 0) => api.get(`/paperwork-templates?client_id=${clientId}&limit=${limit}&offset=${offset}`),
  assign: (id, data) => api.put(`/paperwork-templates/${id}/assign`, data),
  setDefault: (id) => api.put(`/paperwork-templates/${id}/default-letterhead`),
  download: (id) => api.get(`/paperwork-templates/${id}/download`, { responseType: 'blob' }),
  replace: (id, formData) => api.post(`/paperwork-templates/${id}/replace`, formData, { headers: { 'Content-Type': 'multipart/form-data' } }),
  remove: (id) => api.delete(`/paperwork-templates/${id}`),
  preview: (id) => api.get(`/paperwork-templates/${id}/download`, { responseType: 'blob' }),
  resolve: (data) => api.post('/paperwork-templates/resolve', data),
  analyze: (id) => api.post(`/paperwork-templates/${id}/analyze`),
  reviewFields: (id, data) => api.put(`/paperwork-templates/${id}/review-fields`, data),
  approve: (id) => api.post(`/paperwork-templates/${id}/approve`),
};


export const clientStorageApi = {
  tree: (clientId) => api.get(`/client-storage/clients/${clientId}/tree`),
  createFolder: (clientId, data) => api.post(`/client-storage/clients/${clientId}/folders`, data),
  renameFolder: (folderId, data) => api.patch(`/client-storage/folders/${folderId}`, data),
  deleteFolder: (folderId) => api.delete(`/client-storage/folders/${folderId}`),
  upload: (clientId, folderId, file) => { const form = new FormData(); form.append('folder_id', folderId); form.append('file', file); return api.post(`/client-storage/clients/${clientId}/files`, form, { headers: { 'Content-Type': 'multipart/form-data' } }); },
  preview: (clientId, fileId) => api.get(`/client-storage/clients/${clientId}/files/${fileId}/preview`, { responseType: 'blob' }),
  download: (clientId, fileId) => api.get(`/client-storage/clients/${clientId}/files/${fileId}/download`, { responseType: 'blob' }),
  renameFile: (clientId, fileId, data) => api.patch(`/client-storage/clients/${clientId}/files/${fileId}`, data),
  deleteFile: (clientId, fileId) => api.delete(`/client-storage/clients/${clientId}/files/${fileId}`),
};
export const customFormsApi = {
  list: (leadId) => api.get(`/custom-forms/leads/${leadId}/forms`),
  create: (leadId, data) => api.post(`/custom-forms/leads/${leadId}/forms`, data),
  update: (id, data) => api.put(`/custom-forms/forms/${id}`, data),
  duplicate: (id) => api.post(`/custom-forms/forms/${id}/duplicate`),
  share: (id, data = {}) => api.post(`/custom-forms/forms/${id}/share`, data),
  submissions: (leadId) => api.get(`/custom-forms/leads/${leadId}/submissions`),
};
export const publicFormsApi = {
  get: (token) => axios.get(`${API}/custom-forms/share/${token}`),
  submit: (token, answers, files=[]) => { const form = new FormData(); form.append('answers_json', JSON.stringify(answers)); files.forEach(f=>form.append('files', f)); return axios.post(`${API}/custom-forms/share/${token}/submit`, form); },
};

export const salesApi = {
  dashboard: () => api.get('/sales/dashboard'),
  meetingsAligned: (params = {}) => { const qs = new URLSearchParams(Object.fromEntries(Object.entries(params).filter(([, v]) => v))).toString(); return api.get(`/sales/meetings-aligned${qs ? `?${qs}` : ''}`); },
  statuses: () => api.get('/sales/statuses'),
  leadSources: () => api.get('/sales/lead-sources'),
  createLeadSource: (data) => api.post('/sales/lead-sources', data),
  createStage: (pipelineId, data) => api.post(`/sales/pipelines/${pipelineId}/stages`, data),
  listLeads: (params = {}) => {
    const qs = new URLSearchParams(params).toString();
    return api.get(`/sales/leads${qs ? `?${qs}` : ''}`);
  },
  exportLeads: () => api.get('/sales/leads/export', { responseType: 'blob' }),
  downloadLeadImportTemplate: () => api.get('/sales/leads/import-template', { responseType: 'blob' }),
  importLeads: (file) => { const form = new FormData(); form.append('file', file); return api.post('/sales/leads/import', form, { headers: { 'Content-Type': 'multipart/form-data' } }); },
  getLead: (id) => api.get(`/sales/leads/${id}`),
  createLead: (data) => api.post('/sales/leads', data),
  updateLead: (id, data) => api.patch(`/sales/leads/${id}`, data),
  deleteLead: (id) => api.delete(`/sales/leads/${id}`),
  assignLead: (id, data) => api.post(`/sales/leads/${id}/assign`, data),
  qualifyLead: (id,data) => api.post(`/sales/leads/${id}/qualify`, data),
  convertLead: (id) => api.post(`/sales/leads/${id}/convert`),
  listDeals: (params = {}) => {
    const qs = new URLSearchParams(params).toString();
    return api.get(`/sales/deals${qs ? `?${qs}` : ''}`);
  },
  getDeal: (id) => api.get(`/sales/deals/${id}`),
  markWon: (id) => api.post(`/sales/deals/${id}/mark-won`),
  markLost: (id, data) => api.post(`/sales/deals/${id}/mark-lost`, data),
  listPipelines: () => api.get('/sales/pipelines'),
  listStages: (id) => api.get(`/sales/pipelines/${id}/stages`),
  updateDealStage: (id,data) => api.patch(`/sales/deals/${id}/stage`, data),
  listTasks: (params = {}) => { const qs = new URLSearchParams(params).toString(); return api.get(`/sales/tasks${qs ? `?${qs}` : ''}`); },
  createTask: (data) => api.post('/sales/tasks', data),
  updateTask: (id, data) => api.patch(`/sales/tasks/${id}`, data),
  cleanupStaleFollowups: () => api.post('/sales/followups/cleanup-stale'),
  generateFollowups: (relatedId) => api.post(`/sales/followups/generate-schedule?related_id=${relatedId}`),
  listQuotations: (params = {}) => { const qs = new URLSearchParams(params).toString(); return api.get(`/sales/quotations${qs ? `?${qs}` : ''}`); },
  listProposals: (params = {}) => { const qs = new URLSearchParams(params).toString(); return api.get(`/sales/proposals${qs ? `?${qs}` : ''}`); },
  createProposal: (data) => api.post('/sales/proposals', data),
  getProposal: (id) => api.get(`/sales/proposals/${id}`),
  updateProposal: (id, data) => api.patch(`/sales/proposals/${id}`, data),
  deleteProposal: (id) => api.delete(`/sales/proposals/${id}`),
  sendProposal: (id, data = {}) => api.post(`/sales/proposals/${id}/send`, data),
  generateProposalDocument: (id) => api.post(`/sales/proposals/${id}/generate-document`),
  generateProposalPaperworkDocument: (id) => api.post(`/sales/proposals/${id}/generate-paperwork-document`),
  getProposalVersions: (id) => api.get(`/sales/proposals/${id}/versions`),
  previewProposalDocument: (id, versionId) => api.get(`/sales/proposals/${id}/document/preview${versionId ? `?version_id=${versionId}` : ''}`, { responseType: 'blob' }),
  downloadProposalDocument: (id, versionId) => api.get(`/sales/proposals/${id}/document/download${versionId ? `?version_id=${versionId}` : ''}`, { responseType: 'blob' }),
  linkProposalClient: (id, data) => api.post(`/sales/proposals/${id}/client-link`, data),
  retryProposalHandoff: (id) => api.post(`/sales/proposals/${id}/handoff/retry`),
  getSharedProposal: (token) => api.get(`/sales/proposals/share/${token}`),
  downloadSharedProposal: (token) => api.get(`/sales/proposals/share/${token}/download`, { responseType: 'blob' }),
  acceptSharedProposal: (token, data = {}) => api.post(`/sales/proposals/share/${token}/accept`, data),
  requestSharedProposalChanges: (token, data = {}) => api.post(`/sales/proposals/share/${token}/request-changes`, data),
  listNotes: (params = {}) => { const qs = new URLSearchParams(params).toString(); return api.get(`/sales/notes${qs ? `?${qs}` : ''}`); },
  createNote: (data) => api.post('/sales/notes', data),
  listActivities: (params = {}) => { const qs = new URLSearchParams(params).toString(); return api.get(`/sales/activities${qs ? `?${qs}` : ''}`); },
  createActivity: (data) => api.post('/sales/activities', data),
  getRequirementAnalysis: (type, id) => api.get(`/sales/requirement-analysis/${type}/${id}`),
  createRequirementAnalysis: (data) => api.post('/sales/requirement-analysis', data),
  updateRequirementAnalysis: (id, data) => api.patch(`/sales/requirement-analysis/${id}`, data),
  getSolutionPlan: (type, id) => api.get(`/sales/solution-plans/${type}/${id}`),
  createSolutionPlan: (data) => api.post('/sales/solution-plans', data),
  updateSolutionPlan: (id, data) => api.patch(`/sales/solution-plans/${id}`, data),
  listObjections: (params = {}) => { const qs = new URLSearchParams(params).toString(); return api.get(`/sales/objections${qs ? `?${qs}` : ''}`); },
  createObjection: (data) => api.post('/sales/objections', data),
  updateObjection: (id, data) => api.patch(`/sales/objections/${id}`, data),
  getNegotiation: (dealId) => api.get(`/sales/negotiations/${dealId}`),
  createNegotiation: (data) => api.post('/sales/negotiations', data),
  updateNegotiation: (id, data) => api.patch(`/sales/negotiations/${id}`, data),
  getOnboarding: (dealId) => api.get(`/sales/onboarding/${dealId}`),
  linkDealClient: (id, data) => api.post(`/sales/deals/${id}/client-link`, data),
  updateOnboardingTask: (id, data) => api.patch(`/sales/onboarding/tasks/${id}`, data),
  completeOnboardingTask: (id) => api.post(`/sales/onboarding/tasks/${id}/complete`),
  generateOnboardingPaperwork: (id, data = {}) => api.post(`/sales/onboarding/tasks/${id}/paperwork`, data),
  getSettings: () => api.get('/sales/settings'),
  updateFollowupSchedule: (data) => api.patch('/sales/settings/follow-up-schedule', data),
  updateLossReasons: (data) => api.patch('/sales/settings/loss-reasons', data),
  updateProposalTemplate: (data) => api.patch('/sales/settings/proposal-template', data),
  cleanupDemoData: () => api.post('/sales/demo-data/cleanup'),
  getTeam: () => api.get('/sales/team'),
  getSalesRoles: () => api.get('/sales/roles'),
  updateSalesRole: (userId, data) => api.patch(`/sales/team/${userId}/role`, data),
};

export const teamAllocationApi = {
  projects: (params = {}) => api.get(`/projects${new URLSearchParams(params).toString() ? `?${new URLSearchParams(params).toString()}` : ''}`),
  createProject: (data) => api.post('/projects', data),
  createProjectFromDeal: (dealId) => api.post(`/projects/from-deal/${dealId}`),
  archiveProject: (projectId) => api.patch(`/projects/${projectId}/archive`),
  restoreProject: (projectId) => api.patch(`/projects/${projectId}/restore`),
  permanentlyDeleteProject: (projectId) => api.delete(`/projects/${projectId}/permanent`),
  summary: () => api.get('/dashboard/team-allocation-summary'),
  getAllocation: (projectId) => api.get(`/projects/${projectId}/allocation`),
  saveAllocation: (projectId, data) => api.post(`/projects/${projectId}/allocation`, data),
  updateAllocation: (projectId, data) => api.put(`/projects/${projectId}/allocation`, data),
  confirmAllocation: (projectId, data = {}) => api.post(`/projects/${projectId}/allocation/confirm`, data),
  workload: (params = {}) => api.get(`/workload${new URLSearchParams(params).toString() ? `?${new URLSearchParams(params).toString()}` : ''}`),
  projectWorkload: (projectId, params = {}) => api.get(`/projects/${projectId}/workload${new URLSearchParams(params).toString() ? `?${new URLSearchParams(params).toString()}` : ''}`),
  memberWorkload: (userId, params = {}) => api.get(`/team/${userId}/workload${new URLSearchParams(params).toString() ? `?${new URLSearchParams(params).toString()}` : ''}`),
  markLeave: (data) => api.post('/leave', data),
  leave: (params = {}) => api.get(`/leave${new URLSearchParams(params).toString() ? `?${new URLSearchParams(params).toString()}` : ''}`),
  leaveCalendar: () => api.get('/leave/calendar'),
  approveLeave: (leaveId) => api.post(`/leave/${leaveId}/approve`),
  rejectLeave: (leaveId, data) => api.post(`/leave/${leaveId}/reject`, data),
  createTask: (projectId, data) => api.post(`/projects/${projectId}/tasks`, data),
  importPoaTasks: (projectId, assignedTo) => api.post(`/projects/${projectId}/tasks/import-poa${assignedTo ? `?assigned_to=${assignedTo}` : ''}`),
  projectTasks: (projectId, params = {}) => api.get(`/projects/${projectId}/tasks${new URLSearchParams(params).toString() ? `?${new URLSearchParams(params).toString()}` : ''}`),
  projectDeliverables: (projectId) => api.get(`/projects/${projectId}/deliverables`),
  projectActivity: (projectId) => api.get(`/projects/${projectId}/activity`),
  calendarTasks: (params = {}) => api.get(`/tasks/calendar${new URLSearchParams(params).toString() ? `?${new URLSearchParams(params).toString()}` : ''}`),
  myTasks: () => api.get('/tasks/my'),
  getTask: (taskId) => api.get(`/tasks/${taskId}`),
  updateTask: (taskId, data) => api.put(`/tasks/${taskId}`, data),
  createSubtask: (taskId, data) => api.post(`/tasks/${taskId}/subtasks`, data),
  updateSubtask: (taskId, subtaskId, data) => api.put(`/tasks/${taskId}/subtasks/${subtaskId}`, data),
  submitReview: (taskId) => api.post(`/tasks/${taskId}/submit-review`),
  pmApprove: (taskId, data) => api.post(`/tasks/${taskId}/pm-approve`, data),
  pmReject: (taskId, data) => api.post(`/tasks/${taskId}/pm-reject`, data),
  flagBlocker: (taskId, data) => api.post(`/tasks/${taskId}/flag-blocker`, data),
  resolveBlocker: (taskId, data = {}) => api.post(`/tasks/${taskId}/resolve-blocker`, data),
  uploadDeliverable: (taskId, file, notes = '') => { const form = new FormData(); form.append('file', file); form.append('notes', notes); return api.post(`/tasks/${taskId}/deliverables/upload`, form, { headers: { 'Content-Type': 'multipart/form-data' } }); },
  generateTaskContent: (taskId, data) => api.post(`/tasks/${taskId}/generate-content`, data),
  shareTaskToClient: (taskId) => api.post(`/tasks/${taskId}/share-client`),
  taskDeliverables: (taskId) => api.get(`/tasks/${taskId}/deliverables/versions`),
  deleteTaskDeliverable: (taskId, deliverableId) => api.delete(`/tasks/${taskId}/deliverables/${deliverableId}`),
  getDeliverableVersion: (id) => api.get(`/deliverables/${id}`),
  shareClient: (id) => api.post(`/deliverables/${id}/share-client`),
  clientApprove: (id, data) => api.post(`/deliverables/${id}/client-approve`, data),
  clientReject: (id, data) => api.post(`/deliverables/${id}/client-reject`, data),
  compareVersions: (id, versionA, versionB) => api.get(`/deliverables/${id}/compare?version_a=${versionA}&version_b=${versionB}`),
  generateClientPortal: (projectId, data = {}) => api.post(`/projects/${projectId}/client-portal/generate`, data),
  timeline: (projectId) => api.get(`/projects/${projectId}/timeline`),
  projectDashboard: (projectId) => api.get(`/projects/${projectId}/dashboard`),
  notifications: () => api.get('/project-notifications'),
  markNotificationRead: (id) => api.post(`/project-notifications/${id}/read`),
};

export const clientPortalProjectApi = {
  projects: () => api.get('/client-portal/projects'),
  project: (projectId) => api.get(`/client-portal/projects/${projectId}`),
  documents: (projectId) => api.get(`/client-portal/projects/${projectId}/documents`),
  deliverables: (projectId) => api.get(`/client-portal/projects/${projectId}/deliverables`),
  payments: (projectId) => api.get(`/client-portal/projects/${projectId}/payments`),
  surveys: (projectId) => api.get(`/client-portal/projects/${projectId}/surveys`),
};

export const commercialsApi = {
  dashboard: () => api.get('/commercials/dashboard'),
  invoices: (params = {}) => api.get(`/commercials/invoices${new URLSearchParams(params).toString() ? `?${new URLSearchParams(params).toString()}` : ''}`),
  getInvoice: (id) => api.get(`/commercials/invoices/${id}`),
  createInvoice: (data) => api.post('/commercials/invoices', data),
  updateInvoice: (id, data) => api.patch(`/commercials/invoices/${id}`, data),
  deleteInvoice: (id) => api.delete(`/commercials/invoices/${id}`),
  logPayment: (id, data) => api.post(`/commercials/invoices/${id}/payments`, data),
  payments: (id, params = {}) => api.get(`/commercials/invoices/${id}/payments${new URLSearchParams(params).toString() ? `?${new URLSearchParams(params).toString()}` : ''}`),
  createFollowUp: (id, data) => api.post(`/commercials/invoices/${id}/followups`, data),
  followUps: (params = {}) => api.get(`/commercials/followups${new URLSearchParams(params).toString() ? `?${new URLSearchParams(params).toString()}` : ''}`),
  createCreditNote: (id, data) => api.post(`/commercials/invoices/${id}/credit-notes`, data),
  creditNotes: (params = {}) => api.get(`/commercials/credit-notes${new URLSearchParams(params).toString() ? `?${new URLSearchParams(params).toString()}` : ''}`),
  revenueSummary: () => api.get('/commercials/revenue-summary'),
};

export const reviewsApi = {
  dashboard: () => api.get('/reviews/dashboard'),
  list: (params = {}) => api.get(`/reviews${new URLSearchParams(params).toString() ? `?${new URLSearchParams(params).toString()}` : ''}`),
  get: (id) => api.get(`/reviews/${id}`),
  create: (data) => api.post('/reviews', data),
  createPhaseRemark: (data) => api.post('/reviews/phase-remarks', data),
  delete: (id) => api.post(`/reviews/${id}/delete`),
  phases: (params = {}) => api.get(`/reviews/phases${new URLSearchParams(params).toString() ? `?${new URLSearchParams(params).toString()}` : ''}`),
  createPhase: (data) => api.post('/reviews/phases', data),
  updatePhase: (id, data) => api.patch(`/reviews/phases/${id}`, data),
  reorderPhases: (phase_ids) => api.post('/reviews/phases/reorder', { phase_ids }),
  movePhase: (id, phase_id, comments = '') => api.post(`/reviews/${id}/move-phase`, { phase_id, comments }),
  pmApprove: (id, data = {}) => api.post(`/reviews/${id}/pm-approve`, data),
  pmRequestRevision: (id, data) => api.post(`/reviews/${id}/pm-request-revision`, data),
  sendClient: (id, data = {}) => api.post(`/reviews/${id}/send-client`, data),
  clientApprove: (id, data = {}) => api.post(`/reviews/${id}/client-approve`, data),
  clientRequestChanges: (id, data) => api.post(`/reviews/${id}/client-request-changes`, data),
  activity: (id, params = {}) => api.get(`/reviews/${id}/activity${new URLSearchParams(params).toString() ? `?${new URLSearchParams(params).toString()}` : ''}`),
  revisions: (id, params = {}) => api.get(`/reviews/${id}/revisions${new URLSearchParams(params).toString() ? `?${new URLSearchParams(params).toString()}` : ''}`),
};

export const reportsApi = {
  dashboard: (params = {}) => api.get(`/reports/dashboard${new URLSearchParams(params).toString() ? `?${new URLSearchParams(params).toString()}` : ''}`),
  get: (type, params = {}) => api.get(`/reports/${type}${new URLSearchParams(params).toString() ? `?${new URLSearchParams(params).toString()}` : ''}`),
  export: (type, format = 'csv', params = {}) => api.get(`/reports/${type}/export?${new URLSearchParams({ ...params, format }).toString()}`, { responseType: 'blob' }),
  scheduled: () => api.get('/reports/scheduled'),
  createScheduled: (data) => api.post('/reports/scheduled', data),
  updateScheduled: (id, data) => api.patch(`/reports/scheduled/${id}`, data),
  alerts: () => api.get('/reports/alerts'),
  createAlert: (data) => api.post('/reports/alerts', data),
  updateAlert: (id, data) => api.patch(`/reports/alerts/${id}`, data),
};

export const platformAdminApi = {
  dashboard: () => api.get('/platform-admin/dashboard'),
  listAgencies: (params = {}) => api.get(`/platform-admin/agencies${new URLSearchParams(params).toString() ? `?${new URLSearchParams(params).toString()}` : ''}`),
  getAgency: (id) => api.get(`/platform-admin/agencies/${id}`),
  getAgencyUsers: (id, params = {}) => api.get(`/platform-admin/agencies/${id}/users${new URLSearchParams(params).toString() ? `?${new URLSearchParams(params).toString()}` : ''}`),
  getAgencyBilling: (id) => api.get(`/platform-admin/agencies/${id}/billing`),
  getAgencyUsage: (id) => api.get(`/platform-admin/agencies/${id}/usage`),
  getAgencyAuditLogs: (id) => api.get(`/platform-admin/agencies/${id}/audit-logs`),
  suspendAgency: (id, data) => api.post(`/platform-admin/agencies/${id}/suspend`, data),
  reactivateAgency: (id, data) => api.post(`/platform-admin/agencies/${id}/reactivate`, data),
  exportAgency: (id, data) => api.post(`/platform-admin/agencies/${id}/export`, data),
  scheduleDeletion: (id, data) => api.post(`/platform-admin/agencies/${id}/schedule-deletion`, data),
  cancelDeletion: (id, data) => api.post(`/platform-admin/agencies/${id}/cancel-deletion`, data),
  auditLogs: (params = {}) => api.get(`/platform-admin/audit-logs${new URLSearchParams(params).toString() ? `?${new URLSearchParams(params).toString()}` : ''}`),
};

export const hrmsApi = {
  salary: (id) => api.get(`/hrms/employees/${id}/salary`),
  saveSalary: (id, data) => api.put(`/hrms/employees/${id}/salary`, data),
  payroll: (params = {}) => api.get(`/hrms/payroll?${new URLSearchParams(params)}`),
  createPayroll: (data) => api.post("/hrms/payroll", data),
  updatePayroll: (id, data) => api.put(`/hrms/payroll/${id}`, data),
  salarySlip: (id) => api.get(`/hrms/payroll/${id}/salary-slip.pdf`, { responseType: "blob" }),
  dashboard: () => api.get('/hrms/dashboard'),
  employees: (params = {}) => api.get(`/hrms/employees${new URLSearchParams(params).toString() ? `?${new URLSearchParams(params).toString()}` : ''}`),
  employee: (id) => api.get(`/hrms/employees/${id}`),
  createEmployee: (data) => api.post('/hrms/employees', data),
  updateEmployee: (id, data) => api.put(`/hrms/employees/${id}`, data),
  deleteEmployee: (id) => api.delete(`/hrms/employees/${id}`),
  templates: () => api.get('/hrms/templates'),
  template: (id) => api.get(`/hrms/templates/${id}`),
  createTemplate: (data) => api.post('/hrms/templates', data),
  updateTemplate: (id, data) => api.put(`/hrms/templates/${id}`, data),
  drafts: () => api.get('/hrms/drafts'),
  draft: (id) => api.get(`/hrms/drafts/${id}`),
  saveDraft: (data) => api.post('/hrms/drafts', data),
  updateDraft: (id, data) => api.put(`/hrms/drafts/${id}`, data),
  generate: (data) => api.post('/hrms/documents/generate', data),
  documents: () => api.get('/hrms/documents'),
  document: (id) => api.get(`/hrms/documents/${id}`),
  previewUrl: (id) => `${api.defaults.baseURL}/hrms/documents/${id}/preview`,
  downloadPdf: (id) => api.get(`/hrms/documents/${id}/download-pdf`, { responseType: 'blob' }),
  downloadDocx: (id) => api.get(`/hrms/documents/${id}/download-docx`, { responseType: 'blob' }),
  settings: () => api.get('/hrms/settings'),
  updateSettings: (data) => api.patch('/hrms/settings', data),
  uploadSetting: (kind, file) => { const form = new FormData(); form.append('file', file); return api.post(`/hrms/settings/${kind}`, form, { headers: { 'Content-Type': 'multipart/form-data' } }); },
};
